import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';
import Productlist from '../components/Productlist';
import { getCategories, getBanners, getGenderCards } from '../services/api';

const GENDER_LABEL = {
  men: { tag: 'For Him', prefix: "MEN'S" },
  women: { tag: 'For Her', prefix: "WOMEN'S" },
  kids: { tag: 'For Kids', prefix: "KIDS'" },
};

export default function Home() {
  const [currentAd, setCurrentAd] = useState(0);
  const [activeCat, setActiveCat] = useState(null);
  const [categories, setCategories] = useState([]);
  const [catsLoading, setCatsLoading] = useState(true);
  const [heroBanners, setHeroBanners] = useState([]);
  const [promoBanners, setPromoBanners] = useState([]);
  const [genderCards, setGenderCards] = useState([]);
  const [genderLoading, setGenderLoading] = useState(true);

  // ── Hero banners ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getBanners({ section: 'hero' });
        const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
        if (!cancelled) {
          setHeroBanners(data);
          setCurrentAd(0);
        }
      } catch (err) {
        console.error('Hero banners load failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Promo banners (Ad Card 1 & 2) ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getBanners({ section: 'promo' });
        const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
        if (!cancelled) setPromoBanners(data);
      } catch (err) {
        console.error('Promo banners load failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Auto rotate hero ──
  useEffect(() => {
    if (heroBanners.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentAd((prev) => (prev + 1) % heroBanners.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [heroBanners.length]);

  // ── Categories ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getCategories();
        const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
        if (!cancelled) setCategories(data);
      } catch (err) {
        console.error('Failed to load categories', err);
      } finally {
        if (!cancelled) setCatsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Gender cards ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getGenderCards();
        const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
        if (!cancelled) setGenderCards(data);
      } catch (err) {
        console.error('Gender cards load failed', err);
      } finally {
        if (!cancelled) setGenderLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleCategoryClick = (slug) => {
    setActiveCat((prev) => (prev === slug ? null : slug));
  };

  return (
    <div className="vt-home">
      {/* ═══ HERO SLIDER ═══ */}
      {heroBanners.length > 0 && (
        <div className="banner-container">
          <div
            className="banner-track"
            style={{ transform: `translateX(-${currentAd * 100}%)` }}
          >
            {heroBanners.map((ad) => {
              const isDark = ad.theme === 'dark';
              return (
                <div key={ad.id} className={`banner-slide ${isDark ? 'slide-dark' : 'slide-light'}`}>
                  <img src={ad.image_url} alt={ad.title} className="banner-bg-img" />
                  <div className="banner-overlay" />
                  <div className="banner-content">
                    {ad.tag && <div className="banner-tag">{ad.tag}</div>}
                    <h1 className="banner-title">{ad.title}</h1>
                    {ad.subtitle && <p className="banner-subtitle">{ad.subtitle}</p>}
                    <Link to={ad.cta_link || '/shop'} className="banner-cta">
                      {ad.cta_text || 'Shop Now'}
                    </Link>
                  </div>
                  {ad.discount_text && (
                    <div className="banner-discount-badge">
                      <span className="discount-num">{ad.discount_text}</span>
                      {isDark && <span className="discount-label">Today</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {heroBanners.length > 1 && (
            <>
              <button
                className="banner-arrow arrow-left"
                onClick={() => setCurrentAd((p) => (p - 1 + heroBanners.length) % heroBanners.length)}
                aria-label="Previous slide"
              >
                ‹
              </button>
              <button
                className="banner-arrow arrow-right"
                onClick={() => setCurrentAd((p) => (p + 1) % heroBanners.length)}
                aria-label="Next slide"
              >
                ›
              </button>

              <div className="banner-dots">
                {heroBanners.map((_, i) => (
                  <span
                    key={i}
                    className={`dot ${i === currentAd ? 'active' : ''}`}
                    onClick={() => setCurrentAd(i)}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* ═══ SHOP BY STYLE: 2 Promo Cards + Gender Cards ═══ */}
      <section className="gender-section">
        <div className="gender-section-head">
          <h2>Shop by Style</h2>
          
        </div>

        <div className="shop-grid">
          {/* Promo Ad Cards — dynamic from admin */}
          {promoBanners.slice(0, 2).map((ad) => {
            const isDark = ad.theme === 'dark';
            return (
              <div
                key={ad.id}
                className={`ad-card ${isDark ? 'ad-card-dark' : 'ad-card-light'}`}
              >
                <img src={ad.image_url} alt={ad.title} className="ad-card-img" />
                <div className={isDark ? 'ad-card-overlay-dark' : 'ad-card-overlay-light'} />
                <div className="ad-card-content">
                  {ad.tag && (
                    <div className={isDark ? 'ad-card-tag' : 'ad-card-tag-light'}>
                      {ad.tag}
                    </div>
                  )}
                  <div className={isDark ? 'ad-card-title' : 'ad-card-title-light'}>
                    {ad.title}
                  </div>
                  {ad.subtitle && (
                    <div className={isDark ? 'ad-card-sub' : 'ad-card-sub-light'}>
                      {ad.subtitle}
                    </div>
                  )}
                  <Link
                    to={ad.cta_link || '/shop'}
                    className={isDark ? 'ad-btn-dark' : 'ad-btn-light'}
                  >
                    {ad.cta_text || 'Shop Now'}
                  </Link>
                </div>
                {ad.discount_text && (
                  isDark ? (
                    <div className="ad-card-big-num">
                      <span>{ad.discount_text}</span>
                      <small>Off Today</small>
                    </div>
                  ) : (
                    <div className="ad-card-new-badge">{ad.discount_text}</div>
                  )
                )}
              </div>
            );
          })}

          {/* Gender Cards (Men / Women) */}
          {genderLoading
            ? [1, 2].map((n) => (
                <div key={n} className="gender-card gender-skeleton" />
              ))
            : genderCards.map((card, idx) => {
                const meta = GENDER_LABEL[card.gender] || GENDER_LABEL.men;
                return (
                  <Link
                    key={card.id}
                    to={card.link || `/shop?gender=${card.gender}`}
                    className={`gender-card gender-${card.gender} ${
                      idx === 0 ? 'gender-lead' : ''
                    }`}
                    aria-label={`Shop ${card.title}`}
                  >
                    <img
                      src={card.image_url}
                      alt={card.title}
                      className="gender-img"
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src =
                          'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=800&q=80';
                      }}
                    />
                    <div className="gender-overlay" />
                    <div className="gender-content">
                      <span className="gender-tag">{meta.tag}</span>
                      <h3 className="gender-title">
                        {meta.prefix}
                        <br />
                        <span className="gender-title-sub">{card.title}</span>
                      </h3>
                      {card.subtitle && (
                        <p className="gender-subtitle">{card.subtitle}</p>
                      )}
                      <span className="gender-cta">
                        {card.button_text || 'Explore Now'}{' '}
                        <span className="arrow">→</span>
                      </span>
                    </div>
                    <div className="gender-corner">
                      <span className="gender-corner-num">
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                    </div>
                  </Link>
                );
              })}
        </div>
      </section>

      {/* ═══ Categories ═══ */}
      <section className="categories-section">
        <h2>Shop by Category</h2>
        <p className="categories-hint">
          Real looks from our store — tap to filter products
        </p>

        {catsLoading ? (
          <div className="categories-grid">
            {[1, 2, 3].map((n) => (
              <div key={n} className="category-card cat-skeleton" />
            ))}
          </div>
        ) : categories.length === 0 ? (
          <p className="categories-empty">No categories yet.</p>
        ) : (
          <div className="categories-grid">
            {categories.map((cat) => (
              <button
                key={cat.id}
                className={`category-card ${activeCat === cat.slug ? 'active' : ''}`}
                onClick={() => handleCategoryClick(cat.slug)}
              >
                {cat.image_url ? (
                  <img
                    src={cat.image_url}
                    alt={cat.name}
                    className="cat-photo"
                    loading="lazy"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src =
                        'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600&q=70';
                    }}
                  />
                ) : (
                  <div className="cat-photo cat-photo-fallback" />
                )}
                <div className="cat-overlay" />
                <div className="cat-meta">
                  <span className="cat-label">{cat.name}</span>
                  <span className="cat-count">
                    {cat.product_count != null
                      ? `${cat.product_count} ${
                          cat.product_count === 1 ? 'style' : 'styles'
                        }`
                      : 'Shop now'}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      <Productlist category={activeCat} ambedded />
    </div>
  );
}